<template>
  <vs-autocomplete
    :value="value"
    :items="filteredCostCenters"
    item-text="labelWithLinkedInfo"
    item-value="label"
    :label="label"
    :rules="rules"
    required
    free-form-create
    :disabled="disabled"
    @input="handleInput"
  />
</template>

<script setup lang="ts">
import { isEqual } from 'lodash'
import { getSuggestedCostCenters } from './useCostCenterField'
import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import { CostCenter } from '@ironsight/types'
import Division from '@/models/division'

const props = withDefaults(
  defineProps<{
    value?: string | null
    ticketOwnerId?: string | null
    ticketDestination?: number | null
    ticketSources?: number[]
    label?: string | null
    hubId: string
    rules?: Array<any>
    disabled?: boolean
  }>(),
  {
    value: '',
    ticketOwnerId: null,
    ticketDestination: null,
    ticketSources: () => [],
    label: 'Cost center',
    rules: () => [],
    disabled: false,
  },
)

const emit = defineEmits(['input', 'auto-fill'])

const previousCostCenterSuggestions = ref<
  { label: string; labelWithLinkedInfo: string; description: string }[]
>([])
const store = useStore()
const getCostCentersForHub = computed<(hubId: string) => CostCenter[]>(
  () => store.getters['costCenters/getCostCentersForHub'],
)
const getDivisionByUuid = computed<(uuid: string) => Division>(
  () => store.getters['divisions/getByUuid'],
)
const ticketDivision = computed(() =>
  props.ticketOwnerId ? getDivisionByUuid.value(props.ticketOwnerId) : null,
)
const costCentersForHub = computed(() => {
  return getCostCentersForHub.value(props.hubId)
})
const suggestedCostCenters = computed(() => {
  return getSuggestedCostCenters(
    costCentersForHub.value,
    props.ticketSources,
    props.ticketDestination,
    ticketDivision.value?.id,
  )
})

const filteredCostCenters = computed(() => {
  if (suggestedCostCenters.value.length === 0) {
    return costCentersForHub.value.map((costCenter) => {
      return {
        label: costCenter.label,
        labelWithLinkedInfo: costCenter.label,
        description: costCenter.description,
      }
    })
  }
  const costCentersFiltered = [
    { header: 'Suggested' },
    ...suggestedCostCenters.value,
    { header: 'Other' },
  ]

  const suggestedLabels = suggestedCostCenters.value.map((c) => c.label)
  const otherCostCenters = costCentersForHub.value.filter(
    (costCenter) => !suggestedLabels.includes(costCenter.label),
  )

  return costCentersFiltered.concat(
    otherCostCenters.map((costCenter) => {
      return {
        label: costCenter.label,
        labelWithLinkedInfo: costCenter.label,
        description: costCenter.description,
      }
    }),
  )
})

watch(suggestedCostCenters, (newValue) => {
  if (!isEqual(newValue, previousCostCenterSuggestions.value)) {
    previousCostCenterSuggestions.value = newValue
    if (newValue.length === 1) {
      emit('input', newValue[0].label)
    } else if (newValue.length > 1) {
      emit('input', null)
    }
  }
})

onMounted(() => {
  previousCostCenterSuggestions.value = suggestedCostCenters.value
  if (filteredCostCenters.value.length === 1) {
    autoFill(
      'cost-center',
      'label' in filteredCostCenters.value[0] ? filteredCostCenters.value[0].label : null,
    )
  }
})

const handleInput = (newValue) => {
  emit('input', newValue)
}
const autoFill = (field, value) => {
  emit('auto-fill', field, value)
}
</script>
